import { HttpClient } from '@angular/common/http';
import { forkJoin, pipe, EMPTY } from 'rxjs';
import { map, expand, startWith, takeWhile, reduce } from 'rxjs/operators';
import { Page } from '@app/models/page';
import { Query } from '@app/models/queries/queries.model';
import { config } from '@config/config';
import { AuthState } from '@app/auth/auth.state';
import { EnvelopeUtils } from '@app/utils/envelope.utils';
import * as i0 from "@angular/core";
import * as i1 from "@angular/common/http";
import * as i2 from "../auth/auth.state";
export class Fetch {
    constructor(http, authState) {
        this.http = http;
        this.authState = authState;
    }
    /**
     * Makes a single request and returns the API envelope
     */
    get(url) {
        const fullUrl = `${this.constructUrl(url)}`;
        const req = this.http.get(fullUrl);
        return {
            url: fullUrl,
            req: req.pipe(EnvelopeUtils.handleError()),
            models: (modelClass) => req.pipe(map((res) => res.data.map((m) => new modelClass(m))), EnvelopeUtils.handleError()),
            model: (modelClass) => req.pipe(map((res) => new modelClass(res.data[0])), EnvelopeUtils.handleError()),
        };
    }
    /**
     * Makes a single request and returns the API envelope
     */
    getOne(url, id) {
        const fullUrl = `${this.constructUrl(url)}/${id}`;
        const req = this.http.get(fullUrl);
        return {
            url: fullUrl,
            req: req.pipe(EnvelopeUtils.handleError()),
            models: (modelClass) => req.pipe(map((res) => res.data.map((m) => new modelClass(m))), EnvelopeUtils.handleError()),
            model: (modelClass) => req.pipe(map((res) => new modelClass(res.data[0])), EnvelopeUtils.handleError()),
        };
    }
    /**
     * Makes multiple individual requests and strings together the responses
     * by taking the first API envelope, appending the data from every
     * response after, then returning that initial envelope with all the data.
     */
    getSome(url, ids) {
        const fullUrls = ids.map((id) => `${this.constructUrl(url)}/${id}`);
        const requests = [];
        for (const u of fullUrls) {
            requests.push(this.http.get(u));
        }
        const req = forkJoin(requests).pipe(map((envelopes) => envelopes.reduce((acc, val) => {
            acc.data = acc.data.concat(val.data);
            return acc;
        }, envelopes[0])));
        return {
            url: fullUrls,
            req: req.pipe(EnvelopeUtils.handleError()),
            models: (modelClass) => req.pipe(map((res) => res.data.map((m) => new modelClass(m))), EnvelopeUtils.handleError()),
            model: (modelClass) => req.pipe(map((res) => new modelClass(res.data[0])), EnvelopeUtils.handleError()),
        };
    }
    /**
     * Fetches a collection from the API. Limit and offset are calculated from params.
     * @param page The page number to retrieve
     * @param options Any options that are passed as query params
     * but if this param is specified, it would be /utilities/:id/:path
     */
    getPage(url, page, options) {
        const fullUrl = this.constructUrl(url);
        const params = Object.assign({ limit: page.limit, offset: page.offset }, options);
        if (page.sortField && page.sortDirection) {
            params.sort_field = page.sortField;
            params.sort_direction = page.sortDirection;
        }
        const req = this.http.get(fullUrl, { params });
        return {
            url: fullUrl,
            params,
            page,
            req: req.pipe(EnvelopeUtils.handleError()),
            models: (modelClass) => req.pipe(map((res) => res.data.map((m) => {
                const mod = new modelClass(m);
                mod.setTotalCount(res.count);
                return mod;
            })), EnvelopeUtils.handleError()),
            model: (modelClass) => req.pipe(map((res) => {
                const mod = new modelClass(res.data[0]);
                mod.setTotalCount(res.count);
                return mod;
            }), EnvelopeUtils.handleError()),
        };
    }
    /**
     * Fetches all models from the API. Recursively fetches pages of the
     * maximum size and appends them together into one envelope.
     * @param options Any options that are passed as query params
     */
    getAll(url, options) {
        const fullUrl = this.constructUrl(url);
        const page = new Page(1, config.maxPageSize);
        const req = this.getPage('/' + fullUrl, page, options).req.pipe(expand((data) => {
            if (data.pagination && data.pagination.next) {
                const nextPage = Page.fromUrl(data.pagination.next);
                return this.getPage('/' + fullUrl, nextPage, options).req;
            }
            return EMPTY.pipe(startWith(null)); // tslint:disable-line TODO look into the deprecation and update
        }), takeWhile((val) => val !== null), reduce((acc, curr) => {
            acc.data = acc.data.concat(curr.data);
            return acc;
        }));
        return {
            url: fullUrl,
            params: Object.assign({ all: true }, options),
            req: req.pipe(EnvelopeUtils.handleError()),
            models: (modelClass) => req.pipe(map((res) => res.data.map((m) => {
                const mod = new modelClass(m);
                mod.setTotalCount(res.count);
                return mod;
            })), EnvelopeUtils.handleError()),
            model: (modelClass) => req.pipe(map((res) => {
                const mod = new modelClass(res.data[0]);
                mod.setTotalCount(res.count);
                return mod;
            }), EnvelopeUtils.handleError()),
        };
    }
    post(url, body) {
        const fullUrl = this.constructUrl(url);
        return this.http.post(fullUrl, body).pipe(map((res) => {
            res.message = EnvelopeUtils.message(res);
            return res;
        }), EnvelopeUtils.handleError());
    }
    patch(url, id, body) {
        const fullUrl = `${this.constructUrl(url)}/${id}`;
        return this.http.patch(fullUrl, body).pipe(map((res) => {
            res.message = EnvelopeUtils.message(res);
            return res;
        }), EnvelopeUtils.handleError());
    }
    delete(url, id) {
        const fullUrl = `${this.constructUrl(url)}/${id}`;
        return this.http.delete(fullUrl).pipe(map((res) => {
            res.message = EnvelopeUtils.message(res);
            return res;
        }), EnvelopeUtils.handleError());
    }
    /**
     * Fetches one or more queries, combining them efficiently to reduce network requests.
     */
    query(queries) {
        const batchedByDevice = {};
        // Group queries by device id but different query id
        for (let i = 0; i < queries.length; i++) {
            if (!(queries[i] instanceof Query)) {
                queries[i] = new Query(queries[i]);
            }
            const query = queries[i];
            if (batchedByDevice[query.device_id]) {
                batchedByDevice[query.device_id].push(query);
            }
            else {
                batchedByDevice[query.device_id] = [query];
            }
        }
        let requests = [];
        requests = Object.values(batchedByDevice).reduce((acc, batch) => {
            const httpReqs = [];
            for (let i = 0; i < batch.length; i += config.maxQueries) {
                httpReqs.push(this.http.post(this.constructUrl(`/devices/${batch[0].device_id}/query`), {
                    queries: batch.slice(i, i + config.maxQueries).map((query) => query.toRequestBody()),
                }));
            }
            return acc.concat(httpReqs);
        }, []);
        const req = forkJoin(requests).pipe(this.setDataPipe(queries));
        return {
            url: '',
            req: req.pipe(EnvelopeUtils.handleError()),
            models: (modelClass) => req.pipe(map((res) => res.data), EnvelopeUtils.handleError()),
            model: (modelClass) => req.pipe(map((res) => res.data[0]), EnvelopeUtils.handleError()),
        };
    }
    setDataPipe(queries) {
        return pipe(map((entries) => {
            const combinedData = entries.reduce((a, entry) => Object.assign(a, entry.data[0]), {});
            for (const key of Object.keys(combinedData)) {
                const oldQuery = queries.find((query) => query.id === key);
                oldQuery.setData(combinedData[key]);
            }
            return {
                success: true,
                code: 0,
                message: '',
                http_code: 200,
                http_message: '',
                data: queries,
                count: 0,
            };
        }));
    }
    // If url begins with //, don't prepend the base url
    constructUrl(url) {
        if (url.slice(0, 2) === '//') {
            return url.slice(1);
        }
        return `${this.authState.baseURL()}${url}`;
    }
}
Fetch.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function Fetch_Factory() { return new Fetch(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(i2.AuthState)); }, token: Fetch, providedIn: "root" });
